import React, { cloneElement, isValidElement, ReactElement } from 'react';
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { DefaultOptionType } from 'rc-select/lib/Select';
import styled from 'styled-components';
import { TextDisabled } from '../../components/typography/TextDisabled';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';
import { Select, SelectProps } from '../../components/Select';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './FormSelect.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('form-select');
addTranslations(translations);

export interface FormSelectProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name]
> extends Omit<SelectProps<Value>, 'id'> {
  name: Name;
  children?: ReactElement<SelectProps<Value>>;
}

export const filterOption = (input: string, option?: DefaultOptionType) =>
  (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase());

export const FormSelect = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name]
>(
  props: FormSelectProps<FormData, Name, Value>
) => {
  const { t } = useTranslation();
  const {
    name,
    children,
    dropdownStyle,
    notFoundContent = <TextDisabled>{t('no-options')}</TextDisabled>,
    ...rest
  } = props;
  const { control } = useFormContext<FormData>();
  const getIdFromName = useGetIdFromName();
  const { onChange: formOnChange } = useAdditionalFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const { value, disabled } = field;
        const onChange = (
          value: Value,
          option: DefaultOptionType | DefaultOptionType[]
        ) => {
          field.onChange(value ?? null);
          rest?.onChange?.(value, option);
          formOnChange?.();
        };
        const id = getIdFromName(name);
        return children ? (
          isValidElement(children) ? (
            cloneElement(children, {
              disabled,
              ...rest,
              id,
              value,
              onChange,
            })
          ) : (
            children
          )
        ) : (
          <StyledSelect
            disabled={disabled}
            {...rest}
            id={id}
            value={value}
            onChange={onChange}
            styles={{
              popup: { root: { width: 'max-content', ...dropdownStyle } },
            }}
            notFoundContent={notFoundContent}
          />
        );
      }}
    />
  );
};

const StyledSelect = styled(Select<any>)`
  height: auto;
`;
